import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { apiBaseUrl } from "../../constants/urls";
import { getToken } from "../../auth/AuthToken";

const authHeaders = () => ({
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${getToken()}`,
  },
});

const failure = (error, fallback) =>
  error.response?.data || { error: fallback };

// Everything shared with me, across every account: the guest dashboard.
export const fetchReceivedShares = createAsyncThunk(
  "examSummaryShares/fetchReceived",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${apiBaseUrl}/exam_summary_shares`,
        authHeaders()
      );
      return data;
    } catch (error) {
      return rejectWithValue(failure(error, "Failed to load shared reports"));
    }
  }
);

// What I have shared from this account, optionally for one exam.
export const fetchSentShares = createAsyncThunk(
  "examSummaryShares/fetchSent",
  async ({ accountId, examId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${apiBaseUrl}/exam_summary_shares`, {
        ...authHeaders(),
        params: { sent: true, account_id: accountId, exam_id: examId },
      });
      return data;
    } catch (error) {
      return rejectWithValue(failure(error, "Failed to load shares"));
    }
  }
);

// { [accountId]: number of shares not yet opened }, for the account list.
// Counted here from the unopened rows rather than asked of the server.
export const fetchPendingShares = createAsyncThunk(
  "examSummaryShares/fetchPending",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${apiBaseUrl}/exam_summary_shares`, {
        ...authHeaders(),
        params: { unopened: true },
      });
      return data.reduce((counts, share) => {
        counts[share.company_id] = (counts[share.company_id] || 0) + 1;
        return counts;
      }, {});
    } catch (error) {
      return rejectWithValue(failure(error, "Failed to load shares"));
    }
  }
);

export const searchShareRecipients = createAsyncThunk(
  "examSummaryShares/searchRecipients",
  async ({ accountId, query }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${apiBaseUrl}/exam_summary_recipients`,
        { ...authHeaders(), params: { account_id: accountId, q: query } }
      );
      return data;
    } catch (error) {
      return rejectWithValue(failure(error, "Failed to search"));
    }
  }
);

export const markShareOpened = createAsyncThunk(
  "examSummaryShares/markOpened",
  async (shareId, { rejectWithValue }) => {
    try {
      await axios.patch(
        `${apiBaseUrl}/exam_summary_shares/${shareId}`,
        { opened: true },
        authHeaders()
      );
      return shareId;
    } catch (error) {
      return rejectWithValue(failure(error, "Failed to update"));
    }
  }
);

export const revokeShare = createAsyncThunk(
  "examSummaryShares/revoke",
  async (shareId, { rejectWithValue }) => {
    try {
      await axios.delete(
        `${apiBaseUrl}/exam_summary_shares/${shareId}`,
        authHeaders()
      );
      return shareId;
    } catch (error) {
      return rejectWithValue(failure(error, "Failed to revoke"));
    }
  }
);

const initialState = {
  received: [],
  receivedLoading: false,
  receivedError: null,
  sent: [],
  sentLoading: false,
  pendingByAccount: {},
};

const examSummarySharesSlice = createSlice({
  name: "examSummaryShares",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchReceivedShares.pending, (state) => {
        state.receivedLoading = true;
        state.receivedError = null;
      })
      .addCase(fetchReceivedShares.fulfilled, (state, action) => {
        state.receivedLoading = false;
        state.received = action.payload;
      })
      .addCase(fetchReceivedShares.rejected, (state, action) => {
        state.receivedLoading = false;
        state.receivedError = action.payload?.error || "Failed to load shared reports";
      })
      .addCase(fetchSentShares.pending, (state) => {
        state.sentLoading = true;
      })
      .addCase(fetchSentShares.fulfilled, (state, action) => {
        state.sentLoading = false;
        state.sent = action.payload;
      })
      .addCase(fetchSentShares.rejected, (state) => {
        state.sentLoading = false;
      })
      .addCase(fetchPendingShares.fulfilled, (state, action) => {
        state.pendingByAccount = action.payload;
      })
      .addCase(markShareOpened.fulfilled, (state, action) => {
        const share = state.received.find((s) => s.id === action.payload);
        if (share && !share.opened) {
          share.opened = true;
          const left = (state.pendingByAccount[share.company_id] || 1) - 1;
          if (left > 0) state.pendingByAccount[share.company_id] = left;
          else delete state.pendingByAccount[share.company_id];
        }
      })
      .addCase(revokeShare.fulfilled, (state, action) => {
        state.sent = state.sent.filter((s) => s.id !== action.payload);
      });
  },
});

export default examSummarySharesSlice.reducer;
